import './App.css';
import {
  ArrowDownRight,
  ArrowUpRight,
  BrainCircuit,
  Code2,
  Layers3,
  Sparkles,
  Terminal,
} from 'lucide-react';

const services = [
  {
    number: '01',
    icon: Code2,
    title: 'Web development',
    description: 'Cloud-based websites and digital products with a sharp point of view.',
    tags: ['Sites', 'Interfaces'],
  },
  {
    number: '02',
    icon: Layers3,
    title: 'Software development',
    description: 'Custom cloud software that turns complex workflows into clear, dependable tools.',
    tags: ['Systems', 'Automation'],
  },
  {
    number: '03',
    icon: BrainCircuit,
    title: 'AI development',
    description: 'Practical AI tools and intelligent workflows, built around your process.',
    tags: ['AI tools', 'Agents'],
  },
];

function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="Bengal home">Bengal</a>
        <nav className="site-nav" aria-label="Main navigation">
          <a href="#services">What we do</a>
          <a href="#about">About</a>
          <a href="#contact">Contact <ArrowUpRight size={15} /></a>
        </nav>
      </header>

      <main id="top">
        <section className="hero section-wrap">
          <div className="hero-copy">
            <p className="eyebrow"><span className="eyebrow-dot" /> Independent cloud development studio</p>
            <h1>Build something<br /><em>worth using.</em></h1>
            <p className="hero-intro">
              Bengal is a Web, Software, and AI Development company, building real, working things for the cloud.
            </p>
            <div className="hero-actions">
              <a className="button button-dark" href="#contact">Let&apos;s talk <ArrowUpRight size={17} /></a>
              <a className="text-link" href="#services">Explore our work <ArrowDownRight size={17} /></a>
            </div>
          </div>
          <div className="hero-art" aria-label="Bengal development studio graphic">
            <div className="hero-art-grid" />
            <div className="hero-orbit orbit-one" />
            <div className="hero-orbit orbit-two" />
            <div className="hero-card hero-card-top"><Terminal size={16} /><span>ideas → systems</span></div>
            <div className="hero-card hero-card-bottom"><Sparkles size={16} /><span>made with intent</span></div>
            <div className="cat-window"><img src="/imgs/bengal-cat.png" alt="Bengal cat" /></div>
          </div>
        </section>

        <section className="signal-band" aria-label="Bengal services overview">
          <div className="section-wrap signal-inner">
            <span>Web</span><i /> <span>Software</span><i /> <span>AI</span>
            <p>From first sketch to finished product.</p>
          </div>
        </section>

        <section className="services section-wrap" id="services">
          <div className="section-heading">
            <div>
              <p className="eyebrow">What we do</p>
              <h2>Technology with<br /><em>a point of view.</em></h2>
            </div>
            <p className="section-summary">Web, software, and AI - designed and built to work together in the cloud.</p>
          </div>

          <div className="service-grid">
            {services.map(({ number, icon: Icon, title, description, tags }) => (
              <article className="service-card" key={number}>
                <div className="service-topline"><span>{number}</span><Icon size={21} strokeWidth={1.6} /></div>
                <h3>{title}</h3>
                <p>{description}</p>
                <div className="tag-row">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              </article>
            ))}
          </div>
        </section>

        <section className="studio-feature section-wrap" id="about">
          <div className="feature-image">
            <div className="feature-image-inner">
              <span className="feature-stamp">BENGAL<br />STUDIO</span>
              <span className="feature-caption">A little wild.<br />Very well built.</span>
              <div className="feature-line" />
            </div>
          </div>
          <div className="feature-copy">
            <p className="eyebrow">The person behind Bengal</p>
            <h2>Small team.<br /><em>Big attention.</em></h2>
            <p>Bengal was created by William Katz, a developer who likes being close to the whole process: asking the first question, shaping the experience, and writing the code that makes it work.</p>
            <p>From a new web presence to a custom internal tool, we start with the real problem and build from there.</p>
            <div className="creator-line"><span>William Katz</span><span>Creator &amp; Lead Developer</span><a href="http://william.katzkatz.com/" target="_blank" rel="noreferrer">Learn more about William <ArrowUpRight size={14} /></a></div>
            <a className="text-link" href="#contact">Meet Bengal <ArrowUpRight size={17} /></a>
          </div>
        </section>

        <section className="contact section-wrap" id="contact">
          <div className="contact-inner">
            <p className="eyebrow">Start a project</p>
            <h2>Let&apos;s make it<br /><em>real.</em></h2>
            <p>Tell us what you&apos;re working on, what&apos;s getting in the way, or what you wish existed. We&apos;ll take it from there.</p>
            <a className="button button-light" href="mailto:william.katz@me.com">Email William <ArrowUpRight size={17} /></a>
          </div>
          <div className="contact-mark" aria-hidden="true">B</div>
        </section>
      </main>

      <footer className="site-footer section-wrap">
        <div className="footer-top">
          <a className="wordmark" href="#top">Bengal</a>
          <p>Cloud development / Web / Software / AI</p>
          <a href="http://william.katzkatz.com/" target="_blank" rel="noreferrer">William Katz <span className="footer-role">Creator &amp; Lead Developer</span> <ArrowUpRight size={15} /></a>
        </div>
        <div className="footer-bottom"><span>© 2026 Bengal</span><span>Web / Software / AI</span></div>
      </footer>
    </div>
  );
}

export default App;
